import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchPurchaseOrders, formatPoCurrency } from '../services/purchaseOrderService';
import PurchaseOrderCard from './orders/PurchaseOrderCard';
import PurchaseOrderItemSearch from './orders/PurchaseOrderItemSearch';
import BulkSendDraftsButton from './orders/BulkSendDraftsButton';
import ShoppingListView from './ShoppingListView';
import { BTN_SECONDARY, BTN_PRIMARY, FOCUS_RING_CSS as _ORDERS_FOCUS_CSS } from './ordering/orderingStyles';

const STATUS_FILTER_OPTIONS = [
  { value: 'active_open', label: 'Active / Open (default)' },
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Drafts' },
  { value: 'sent_family', label: 'Sent' },
  { value: 'received_closed', label: 'Received / Closed' },
  { value: 'canceled_voided', label: 'Canceled / Voided / Replaced' },
  { value: 'archived', label: 'Archived' },
];

const SHOW_DEFAULT_DAYS = 7;

const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

const startOfDayIso = (yyyyMmDd) => {
  if (!yyyyMmDd) return null;
  const d = new Date(`${yyyyMmDd}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const endOfDayIso = (yyyyMmDd) => {
  if (!yyyyMmDd) return null;
  const d = new Date(`${yyyyMmDd}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const todayYmd = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const daysAgoYmd = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

// Deep-link support: another page (e.g. the vendor order-history modal) can link
// to orders.html?po=<id>&poNumber=<number> to reopen a specific purchase order.
// When present we widen the filters so the target PO is guaranteed to load, seed
// the search with its number, and hand the id to the matching card so it opens.
const readDeepLinkPo = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return { poId: params.get('po') || '', poNumber: params.get('poNumber') || '' };
  } catch (_) {
    return { poId: '', poNumber: '' };
  }
};

export default function OrdersPage({ storeId, storeName, storeAddress = '', userEmail = '', userName = '' }) {
  // Evaluated once on mount; window.location is stable for the page lifetime.
  const deepLinkPo = readDeepLinkPo();
  const hasDeepLink = !!deepLinkPo.poId;
  const [autoExpandId] = useState(deepLinkPo.poId || null);

  const [fromDate, setFromDate] = useState(hasDeepLink ? '' : daysAgoYmd(SHOW_DEFAULT_DAYS - 1));
  const [toDate, setToDate] = useState(hasDeepLink ? '' : todayYmd());
  const [searchTerm, setSearchTerm] = useState(hasDeepLink ? deepLinkPo.poNumber : '');
  const [statusFilter, setStatusFilter] = useState(hasDeepLink ? '' : 'active_open');
  const [vendorFilter, setVendorFilter] = useState('');
  // A deep link may target a superseded revision (is_latest_revision === false,
  // e.g. an amended order shown in the vendor history modal). Don't hide those
  // when we arrived via a PO link, or the linked card would never render.
  const [showOnlyLatest, setShowOnlyLatest] = useState(!hasDeepLink);
  // Page-level section: delivery-vendor purchase orders vs pickup-vendor
  // shopping lists. PO behavior is unchanged when 'orders' is active.
  const [section, setSection] = useState('orders');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  // Refresh when Counter Intelligence edits a draft order from the assistant
  // drawer (it dispatches these after a successful apply), so the change shows
  // up without a manual refresh.
  useEffect(() => {
    const onChanged = () => reload();
    window.addEventListener('purchaseOrdersChanged', onChanged);
    window.addEventListener('counter-intelligence:applied', onChanged);
    return () => {
      window.removeEventListener('purchaseOrdersChanged', onChanged);
      window.removeEventListener('counter-intelligence:applied', onChanged);
    };
  }, [reload]);

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    const filters = {
      fromIso: startOfDayIso(fromDate) || undefined,
      toIso: endOfDayIso(toDate) || undefined,
      search: searchTerm.trim() || undefined,
      status: statusFilter || undefined,
      limit: 250,
    };
    fetchPurchaseOrders(storeId, filters)
      .then((data) => {
        if (cancelled) return;
        setRows(data || []);
      })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Failed to load purchase orders.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [storeId, fromDate, toDate, searchTerm, statusFilter, reloadToken]);

  const vendorOptions = useMemo(() => {
    const set = new Map();
    rows.forEach((row) => {
      const name = row.vendor_name_snapshot || 'Unknown';
      if (!set.has(name)) set.set(name, name);
    });
    return [{ value: '', label: 'All vendors' }, ...Array.from(set.entries()).map(([value, label]) => ({ value, label }))];
  }, [rows]);

  const visibleRows = useMemo(() => {
    return rows.filter((row) => {
      if (vendorFilter && row.vendor_name_snapshot !== vendorFilter) return false;
      if (showOnlyLatest && row.is_latest_revision === false) return false;
      return true;
    });
  }, [rows, vendorFilter, showOnlyLatest]);

  const totals = useMemo(() => {
    const EXCLUDED_FROM_TOTALS = ['draft', 'canceled', 'replaced', 'voided'];
    const includedRows = visibleRows.filter((r) => !EXCLUDED_FROM_TOTALS.includes(r.delivery_status));
    const total = includedRows.reduce((sum, r) => sum + Number(r.total || 0), 0);
    const sent = visibleRows.filter((r) => ['sent', 'delivered', 'opened', 'clicked'].includes(r.delivery_status)).length;
    const excludedCount = visibleRows.length - includedRows.length;
    return { total, sent, count: visibleRows.length, excludedCount };
  }, [visibleRows]);

  if (!storeId) {
    return (
      <div style={shellStyle} className="orders-shell">
        <div style={{ padding: 24, color: '#8B7B73' }}>Select a store to view purchase orders.</div>
      </div>
    );
  }

  return (
    <div style={shellStyle} className="orders-shell">
      <style>{ordersPageMobileCss}</style>
      <header style={headerStyle} className="orders-header">
        <div>
          <h1 style={titleStyle} className="orders-title">Purchase Orders</h1>
          <div style={subtitleStyle}>
            {storeName ? `${storeName} · ` : ''}Purchase orders sent from this store. Click any card to expand.
          </div>
        </div>
        {section === 'orders' && (
        <div style={metricsRowStyle} className="orders-metrics-row">
          <Metric label="Showing" value={String(totals.count)} />
          <Metric label="Sent successfully" value={String(totals.sent)} />
          <Metric
            label="Total value"
            value={formatPoCurrency(totals.total)}
            highlight
            note={totals.excludedCount > 0 ? `Excl. ${totals.excludedCount} draft/canceled/replaced/voided` : null}
          />
        </div>
        )}
      </header>

      <div style={tabBarStyle} className="orders-tab-bar">
        <button type="button" style={tabPillStyle(section === 'orders')} onClick={() => setSection('orders')}>
          Purchase orders
        </button>
        <button type="button" style={tabPillStyle(section === 'shopping')} onClick={() => setSection('shopping')}>
          Shopping lists
        </button>
      </div>

      {section === 'orders' && (<>

      <section style={filtersCardStyle} className="orders-filters-card">
        <div style={filterRowStyle} className="orders-filter-row">
          <div style={filterFieldStyle}>
            <label style={filterLabelStyle}>Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Vendor or PO number…"
              style={textInputStyle}
              className="orders-input"
            />
          </div>
          <div style={filterFieldStyle}>
            <label style={filterLabelStyle}>From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={textInputStyle} className="orders-input" />
          </div>
          <div style={filterFieldStyle}>
            <label style={filterLabelStyle}>To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={textInputStyle} className="orders-input" />
          </div>
          <div style={filterFieldStyle}>
            <label style={filterLabelStyle}>Vendor</label>
            <select value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)} style={textInputStyle} className="orders-input">
              {vendorOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div style={filterFieldStyle}>
            <label style={filterLabelStyle}>Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={textInputStyle} className="orders-input">
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={quickRowStyle} className="orders-quick-row">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" style={pillBtnStyle} onClick={() => { setFromDate(daysAgoYmd(6)); setToDate(todayYmd()); }}>Last 7 days</button>
            <button type="button" style={pillBtnStyle} onClick={() => { setFromDate(daysAgoYmd(29)); setToDate(todayYmd()); }}>Last 30 days</button>
            <button type="button" style={pillBtnStyle} onClick={() => { setFromDate(daysAgoYmd(89)); setToDate(todayYmd()); }}>Last 90 days</button>
            <button type="button" style={pillBtnStyle} onClick={() => { setFromDate(''); setToDate(''); }}>All time</button>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#5A4A42', fontSize: 13 }}>
            <input type="checkbox" checked={showOnlyLatest} onChange={(e) => setShowOnlyLatest(e.target.checked)} />
            Hide superseded revisions
          </label>
          <button type="button" style={refreshBtnStyle} onClick={reload}>Refresh</button>
        </div>
      </section>

      <PurchaseOrderItemSearch storeId={storeId} />

      <BulkSendDraftsButton rows={visibleRows} onChanged={reload} />

      <section>
        {loading && <div style={emptyStateStyle}>Loading purchase orders…</div>}
        {error && <div style={errorStateStyle}>{error}</div>}
        {!loading && !error && visibleRows.length === 0 && (
          <div style={emptyStateStyle}>
            No purchase orders match these filters. Try widening the date range or clearing search.
          </div>
        )}
        {!loading && !error && visibleRows.map((row) => (
          <PurchaseOrderCard
            key={row.id}
            summary={row}
            storeId={storeId}
            storeName={storeName}
            storeAddress={storeAddress}
            userEmail={userEmail}
            userName={userName}
            autoExpandPoId={autoExpandId}
            onChanged={reload}
            onOpenLinkedPo={(linkedPoId, linkedPoNumber) => {
              // Reveal the linked PO by switching to "All statuses", clearing date
              // range, and searching by PO number so the linked card loads and is visible.
              setStatusFilter('');
              setFromDate('');
              setToDate('');
              if (linkedPoNumber) setSearchTerm(linkedPoNumber);
            }}
          />
        ))}
      </section>
      </>)}
      {section === 'shopping' && (
        <ShoppingListView storeId={storeId} />
      )}
    </div>
  );
}

const Metric = ({ label, value, highlight, note }) => (
  <div style={{ minWidth: 130 }}>
    {note && <div style={{ fontSize: 10, color: '#8B7B73', marginBottom: 1, fontStyle: 'italic' }}>{note}</div>}
    <div style={{ fontSize: 10, fontWeight: 700, color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 18, color: highlight ? '#526B7C' : '#3D2518', marginTop: 2 }}>{value}</div>
  </div>
);

const shellStyle = { padding: '24px 28px', maxWidth: 1280, margin: '0 auto', fontFamily: 'Open Sans, sans-serif', color: '#3D2518' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' };
const titleStyle = { fontFamily: 'Montserrat, sans-serif', fontSize: 28, fontWeight: 800, color: '#3D2518', margin: 0 };
const subtitleStyle = { color: '#8B7B73', fontSize: 14, marginTop: 4 };
const metricsRowStyle = { display: 'flex', gap: 22, padding: '12px 16px', background: '#fff', border: '1.5px solid #E9DFD6', borderRadius: 12 };
const filtersCardStyle = { background: '#fff', border: '1.5px solid #E9DFD6', borderRadius: 12, padding: 14, marginBottom: 12 };
const filterRowStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 };
const filterFieldStyle = { display: 'flex', flexDirection: 'column' };
const filterLabelStyle = { fontSize: 11, fontWeight: 700, color: '#5A4A42', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 };
const textInputStyle = { padding: '8px 10px', border: '1.5px solid #E9DFD6', borderRadius: 8, fontSize: 13, color: '#3D2518', background: '#fff' };
const quickRowStyle = { marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between' };
const pillBtnStyle = { ...BTN_SECONDARY, padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600 };
const refreshBtnStyle = { ...BTN_PRIMARY, padding: '6px 14px', fontSize: 12 };
const tabBarStyle = { display: 'flex', gap: 6, marginBottom: 12 };
const tabPillStyle = (active) => ({
  ...BTN_SECONDARY,
  padding: '7px 16px',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 700,
  minHeight: 36,
  background: active ? '#5A4A42' : '#fff',
  color: active ? '#fff' : '#5A4A42',
  border: `1.5px solid ${active ? '#5A4A42' : '#d5c9c0'}`,
});
const emptyStateStyle = { padding: 32, textAlign: 'center', color: '#8B7B73', background: '#fff', border: '1.5px dashed #E9DFD6', borderRadius: 12 };
const errorStateStyle = { padding: 16, color: '#b71c1c', background: '#fdecea', borderRadius: 10, border: '1px solid #f5c2bd' };

const ordersPageMobileCss = `
  ${_ORDERS_FOCUS_CSS}
  @media (max-width: 640px) {
  .orders-shell {
    padding: 16px 14px !important;
  }
  .orders-title {
    font-size: 22px !important;
  }
  .orders-header {
    flex-direction: column !important;
    align-items: flex-start !important;
  }
  .orders-metrics-row {
    width: 100% !important;
    box-sizing: border-box !important;
    flex-direction: column !important;
    gap: 10px !important;
    padding: 10px 12px !important;
  }
  .orders-filters-card {
    padding: 12px !important;
  }
  .orders-filter-row {
    grid-template-columns: 1fr !important;
  }
  .orders-input {
    width: 100% !important;
    box-sizing: border-box !important;
    font-size: 16px !important;
  }
  .orders-quick-row {
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
    scrollbar-width: none !important;
    gap: 6px !important;
    justify-content: flex-start !important;
    padding-bottom: 4px !important;
  }
  .orders-quick-row::-webkit-scrollbar { display: none !important; }
  .orders-quick-row button {
    flex-shrink: 0 !important;
    white-space: nowrap !important;
    min-height: 36px !important;
  }
}
`;
